import { useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import ImageField from '../FormLegacy/FormFieldsV2/ImageField'
import TextField from '../FormLegacy/FormFieldsV2/TextField'
import RadioField from '../FormLegacy/FormFieldsV2/RadioField'
import TextAreaField from '../FormLegacy/FormFieldsV2/TextAreaField'
import {
  addNeighbourhoodPerks,
  updateNeighbourhoodPerks,
  deleteNeighbourhoodPerks,
} from '../../reducers/Organisation/neighbourhoodPerksReducer'
import { getAssetLink } from '../../helpers/get-asset-link'
import DropdownList from '../Calendar/Sidebar/DropdownList'

import categories from './categories'
import { validationSchema } from './helpers'

const PerksForm = ({ buildingId, close, perksData }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['neighborhood-perks'])
  const [selectActive, setSelectActive] = useState(false)

  const deleteHandler = async (id) => {
    await dispatch(deleteNeighbourhoodPerks(id))
    close()
  }

  const toggleActive = () => {
    setSelectActive((prevSelectActive) => !prevSelectActive)
  }

  const handleSelect = (_, item, setFieldValue, currentCategory) => {
    const index = currentCategory.map((el) => el.id).indexOf(item.id)

    if (index < 0) {
      setFieldValue('category', [item])
      toggleActive()
    } else {
      setFieldValue('category', [])
    }
  }

  const handleFormSubmit = async (
    { category, description, image, logo, name, perkId, perkType, link, tag, url },
    { setSubmitting, setStatus },
  ) => {
    const newPerksData = {
      buildingId,
      name,
      description,
      tag,
      category: category[0].text,
    }

    if (perkType === 'url') {
      newPerksData.url = url
    } else {
      newPerksData.link = link
    }

    if (logo.file) {
      newPerksData.logo = logo.file
    }

    if (image.file) {
      newPerksData.image = image.file
    }

    setSubmitting(true)
    setStatus({ formError: '' })

    try {
      if (!perksData) {
        await dispatch(addNeighbourhoodPerks(newPerksData))
      } else {
        await dispatch(updateNeighbourhoodPerks(newPerksData, perkId))
      }
      setSubmitting(false)
      setStatus({ formError: '' })
      close()
    } catch {
      setSubmitting(false)
      setStatus({
        formError: t('neighborhood-perks:form.serverError'),
      })
    }
  }

  const getInitValues = () => {
    return {
      image: {
        file: false,
        image: perksData
          ? getAssetLink({
              path: 'chitown-images/neighbourhood',
              file: perksData.image,
              fallback: { file: 'neighbourhood_image.png' },
            })
          : '',
      },
      category: perksData ? [categories.find((item) => item.text === perksData.category)] : [],
      description: perksData ? perksData.description : '',
      tag: perksData ? perksData.tag : '',
      logo: {
        file: false,
        image: perksData
          ? getAssetLink({
              path: 'chitown-images/neighbourhood',
              file: perksData.logo,
              fallback: { file: 'neighbourhood_image.png' },
            })
          : '',
      },
      name: perksData ? perksData.name : '',
      perkId: perksData ? perksData.id : '',
      perkType: perksData && perksData.url ? 'url' : 'link',
      link: perksData ? perksData.link : null,
      url: perksData ? perksData.url : null,
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitValues()}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      initialStatus={{ updateMessage: false, formError: '' }}
    >
      {({
        handleChange,
        handleSubmit,
        values,
        errors,
        touched,
        setFieldTouched,
        isSubmitting,
        status,
        isValid,
        dirty,
        setFieldValue,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <div className={`ctp-building`}>
              <div className="form">
                <div className="columns">
                  <div className="column">
                    <ImageField
                      name={'logo'}
                      placeholderImage={getAssetLink({
                        path: 'chitown-images/neighbourhood',
                        fallback: { file: 'neighbourhood_image.png' },
                      })}
                      additionalClass={'field-image--building-logo'}
                      label={t('neighborhood-perks:form.logo.label')}
                      imageRatio={16 / 9}
                      value={values.logo}
                      onChange={handleChange}
                    />
                    <TextField
                      name={'name'}
                      label={t('neighborhood-perks:form.name.label')}
                      placeholder={t('neighborhood-perks:form.name.placeholder')}
                      onChange={handleChange}
                      value={values.name}
                      isError={touched.name && !!errors.name}
                      onBlur={() => setFieldTouched('name')}
                    />
                    <TextField
                      name={'tag'}
                      label={t('neighborhood-perks:form.tag.label')}
                      placeholder={t('neighborhood-perks:form.tag.placeholder')}
                      onChange={handleChange}
                      value={values.tag}
                      isError={touched.tag && !!errors.tag}
                      onBlur={() => setFieldTouched('tag')}
                      maxLength="4"
                    />

                    <div className="perk-type-container">
                      <label className="label">{t('neighborhood-perks:form.perkType.label')}</label>
                      <div className="perk-type">
                        <RadioField
                          additionalClass="perk-radio"
                          checked={values.perkType === 'link'}
                          label={t('neighborhood-perks:form.perkType.options.promoCode')}
                          name="perkType"
                          type="radio"
                          value="link"
                          onChange={(e) => setFieldValue('perkType', e.target.value)}
                        />
                        <RadioField
                          additionalClass="perk-radio"
                          checked={values.perkType === 'url'}
                          label={t('neighborhood-perks:form.perkType.options.websiteUrl')}
                          name="perkType"
                          type="radio"
                          value="url"
                          onChange={(e) => setFieldValue('perkType', e.target.value)}
                        />
                      </div>
                    </div>
                    {values.perkType === 'url' && (
                      <TextField
                        isError={touched.url && !!errors.url}
                        name="url"
                        onBlur={() => setFieldTouched('url')}
                        onChange={handleChange}
                        placeholder={t('neighborhood-perks:form.url.placeholder')}
                        value={values.url}
                      />
                    )}
                    {values.perkType === 'link' && (
                      <TextField
                        isError={touched.link && !!errors.link}
                        name="link"
                        onBlur={() => setFieldTouched('link')}
                        onChange={handleChange}
                        placeholder={t('neighborhood-perks:form.link.placeholder')}
                        value={values.link}
                      />
                    )}
                    <TextAreaField
                      isError={touched.description && !!errors.description}
                      label={t('neighborhood-perks:form.description.label')}
                      maxLength="510"
                      name="description"
                      onChange={handleChange}
                      placeholder={t('neighborhood-perks:form.description.placeholder')}
                      value={values.description}
                    />
                  </div>
                  <div className="column">
                    <ImageField
                      additionalClass={'field-image--big'}
                      imageRatio={16 / 9}
                      label={t('neighborhood-perks:form.image.label')}
                      name={'image'}
                      onChange={handleChange}
                      placeholderImage={getAssetLink({
                        path: 'chitown-images/neighbourhood',
                        fallback: { file: 'neighbourhood_image.png' },
                      })}
                      value={values.image}
                    />
                    <DropdownList
                      explicit
                      activate={toggleActive}
                      filters={values.category}
                      isActive={selectActive}
                      items={categories}
                      label={t('neighborhood-perks:form.category.label')}
                      name="Choose Category"
                      options={{
                        hasSearch: false,
                        hasSelection: true,
                        hasSelectAll: false,
                        selectAllByDefault: false,
                        allowMultiple: false,
                      }}
                      onSelect={(name, item) =>
                        handleSelect(name, item, setFieldValue, values.category)
                      }
                    />
                  </div>
                </div>
                <div className="columns form__actions">
                  {perksData && (
                    <div className="column">
                      <button
                        className="button"
                        onClick={() => deleteHandler(perksData.id)}
                        type="button"
                      >
                        {t('neighborhood-perks:form.delete')}
                      </button>
                    </div>
                  )}
                  <div className="column">
                    <button className="button" onClick={close} type="button">
                      {t('neighborhood-perks:form.cancel')}
                    </button>
                  </div>
                  <div className="column">
                    <button
                      className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                      disabled={!isValid || !dirty || isSubmitting}
                      type="submit"
                    >
                      {perksData
                        ? t('neighborhood-perks:form.save')
                        : t('neighborhood-perks:form.create')}
                    </button>
                    <label className="help is-danger">{status.formError}</label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

PerksForm.propTypes = {
  buildingId: PropTypes.string.isRequired,
  close: PropTypes.func,
  perksData: PropTypes.object,
}

export default PerksForm
